import React, { ReactNode } from "react";
import { mdiClose } from "@mdi/js";
import { useTheme } from "../../util";
import { Button, ButtonVariant } from "../../atoms/Button";
import { ThemeColor } from "../../props";

export interface SnackbarProps {
  /**
   * Content to display inside the snackbar.
   * Typically contains notification messages, status updates, or brief information.
   */
  children: ReactNode;

  /**
   * Additional CSS class names to apply to the snackbar container.
   */
  className?: string;

  /**
   * Controls whether the snackbar is visible.
   * When true, the snackbar slides up from the bottom with a fade-in animation.
   * When false, the snackbar slides down and fades out.
   */
  visible: boolean;

  /**
   * Theme color variant for the snackbar styling.
   * Should be a value from the imported `ThemeColor` enum.
   * Different variants provide appropriate colors for various notification types.
   * @default ThemeColor.none
   */
  variant?: ThemeColor;

  /**
   * Callback function called when the close button is clicked.
   * When provided, displays a close button on the right side of the snackbar.
   * Set to null to hide the close button entirely.
   * @default null
   */
  onClose?: (() => void) | null;
}

/**
 * Snackbar component for displaying temporary notification messages at the bottom of the screen.
 * Provides a fixed-position container that slides up from the bottom with smooth animations.
 * Supports different color variants for various notification types (success, error, warning, info)
 * and includes an optional close button for user-dismissible notifications.
 */
export const Snackbar = ({
  children,
  className = "",
  visible,
  variant = ThemeColor.none,
  onClose = null,
}: SnackbarProps) => {
  const { isGlobalDarkMode } = useTheme();

  const baseClasses =
    "fixed bottom-5 left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-md shadow-sm text-white transition-transform duration-300 ease-in-out flex items-center gap-3";

  let variantClasses: string;

  switch (variant) {
    case ThemeColor.error:
    case ThemeColor.danger:
      variantClasses =
        "dark bg-error-600 text-front-50 dark:bg-error-700 dark:text-front-50";
      break;
    case ThemeColor.warning:
    case ThemeColor.alert:
      variantClasses =
        "dark bg-warning-600 text-front-50 dark:bg-warning-700 dark:text-front-50";
      break;
    case ThemeColor.success:
    case ThemeColor.done:
      variantClasses =
        "dark bg-success-600 text-front-50 dark:bg-success-700 dark:text-front-50";
      break;
    default:
      variantClasses = `${isGlobalDarkMode ? "light" : "dark"} bg-back-600 text-front-200 dark:bg-back-100 dark:text-front-900`;
  }

  const visibilityClasses = visible
    ? "translate-y-0 opacity-100"
    : "translate-y-full translate-y-4 opacity-0";

  return (
    <div
      className={`${baseClasses} ${variantClasses} ${visibilityClasses} ${className}`}
    >
      {children}
      {onClose && (
        <Button
          onClick={onClose}
          iconPath={mdiClose}
          color={ThemeColor.transparent}
          variant={ButtonVariant.text}
        />
      )}
    </div>
  );
};

Snackbar.displayName = "Snackbar";
